import { Box } from '@material-ui/core'
import classNames from 'classnames'
import { format } from 'date-fns'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { createYupResolver } from 'src/helpers/custom-yup-resolver'
import generateRandomId from 'src/helpers/generate-random-id'
import {
  Button,
  FormattedText,
  FormControl,
  Icon,
  Loader,
  Modal,
  PhoneNumberField,
  TextField,
  Typography,
} from 'src/modules/core/components'
import { MessageInput } from 'src/modules/core/components/message-input/message-input'
import { getDefaultCountryCode, getPhoneAndCountryCode } from 'src/modules/core/utils/phone-input.utils'
import { eventEditSchema } from 'src/modules/event/schemas/event-edit.schema'
import { formatEventDate } from 'src/modules/event/utils/helpers'
import { generateEventMessage } from 'src/modules/event/utils/message-builder'
import { resolveProfileMessageLanguage } from 'src/modules/user/utils/resolve-profile-message-language'
import classes from './send-test-reminder-form-modal.module.scss'

import { maxMessageLength } from '@shared/core/events'
import { SendTestReminderPayload } from '@shared/core/test-reminders'
import { useCalendars } from 'src/modules/calendar/hooks/use-calendars'
import { useValidatePhoneNumbers } from 'src/modules/sms/sms-compliance/hooks/use-sms-compliance'
import type {
  SendTestReminderForm,
  SendTestReminderFormData,
  SendTestReminderModalContentProps,
  SendTestReminderModalProps,
} from './send-test-reminder-form-modal.interface'

const MAX_RECEIVER_NUMBERS_AMOUNT = 10

export const SendTestReminderFormModal: FC<SendTestReminderModalProps> = ({
  onClose,
  modalTitle,
  isLoading,
  ...props
}) => {
  if (isLoading) {
    return (
      <Modal
        style={{ maxWidth: '720px' }}
        onClose={onClose}
        title={
          <Typography variant="h3" className={classes.modalTitle}>
            {modalTitle}
          </Typography>
        }
      >
        <Loader isLoading={isLoading} />
      </Modal>
    )
  }
  return <SendTestReminderModalContent onClose={onClose} modalTitle={modalTitle} {...props} />
}

export const SendTestReminderModalContent: FC<SendTestReminderModalContentProps> = ({
  profileData,
  preferences,
  event,
  onClose,
  onSend,
  modalTitle,
  error,
}) => {
  const defaultCountryCode = getDefaultCountryCode(profileData)

  const calendars = useCalendars()
  const calendar = calendars.data?.find((calendar) => calendar.id === event.calendar)

  const defaultReceiverNumbers = useMemo(
    () =>
      profileData.usage_mode === 'personal'
        ? profileData.phone_numbers
        : (event?.receiver_numbers?.filter(Boolean) ?? []),
    [profileData, event],
  )

  if (defaultReceiverNumbers.length === 0) defaultReceiverNumbers.push('')

  const {
    register,
    handleSubmit,
    control,
    watch,
    setError,
    setValue,
    formState: { errors },
  } = useForm<SendTestReminderForm>({
    defaultValues: {
      reminder_enabled: true,
      reminder_message: event?.reminder_message || profileData?.default_message_template.reminder_message || '',
      location: event?.location || profileData?.default_message_template.location || '',
      receiver_numbers: defaultReceiverNumbers.map((n) => ({
        id: generateRandomId(),
        ...getPhoneAndCountryCode(n, getDefaultCountryCode(profileData)),
      })),
    },
    resolver: createYupResolver(eventEditSchema),
  })

  const [amountOfReceiverNumbers] = useState(defaultReceiverNumbers.length || 1)

  // State to force re-render of phone number fields
  const [phoneNumberKey, setPhoneNumberKey] = useState(0)

  // Phone validation state
  const [phoneValidationErrors, setPhoneValidationErrors] = useState<Record<number, string>>({})
  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [phoneNumbersToValidate, setPhoneNumbersToValidate] = useState<string[]>([])

  const { formatMessage } = useIntl()
  const { location, reminder_message } = watch()

  // Watch phone number fields specifically for validation
  const watchedPhoneNumbers = watch('receiver_numbers')

  // Extract phone numbers for validation
  const phoneNumbersForValidation = useMemo(() => {
    return (
      watchedPhoneNumbers?.map((n) => n.phone).filter((phone) => phone && phone.trim() !== '' && phone !== '+') || []
    )
  }, [watchedPhoneNumbers])

  // Debounced validation effect
  useEffect(() => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current)
    }

    if (phoneNumbersForValidation.length === 0) {
      setPhoneValidationErrors({})
      setPhoneNumbersToValidate([])
      return
    }

    validationTimeoutRef.current = setTimeout(() => {
      setPhoneNumbersToValidate(phoneNumbersForValidation)
    }, 300)

    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }
    }
  }, [phoneNumbersForValidation])

  // Use the validation hook
  const { data: validationResults } = useValidatePhoneNumbers(phoneNumbersToValidate, {
    enabled: phoneNumbersToValidate.length > 0,
  })

  // Process validation results and map to field indices
  useEffect(() => {
    if (!validationResults || !watchedPhoneNumbers) {
      return
    }

    const newErrors: Record<number, string> = {}

    validationResults.forEach((result) => {
      if (!result.isValid && result.invalidReason) {
        const fieldIndex = watchedPhoneNumbers.findIndex((field) => {
          const fieldPhone = field.phone || ''
          const resultPhone = result.phoneNumber || ''
          return fieldPhone === resultPhone
        })

        if (fieldIndex !== -1) {
          newErrors[fieldIndex] = result.errorMessage
        }
      }
    })

    setPhoneValidationErrors(newErrors)
  }, [validationResults, watchedPhoneNumbers])

  const eventDate = useMemo(() => new Date(parseInt(event.event_date, 10)), [event.event_date])

  const {
    message: messagePreview,
    messageLength,
    segmentsCount,
  } = useMemo(
    () =>
      generateEventMessage(
        'reminder',
        profileData,
        preferences,
        { ...event, reminder_message, location },
        calendar,
        event.template_language,
      ),
    [profileData, preferences, reminder_message, event, location, calendar],
  )
  const messageLanguage = useMemo(
    () => resolveProfileMessageLanguage(profileData, event.template_language).language,
    [profileData, event.template_language],
  )

  const {
    fields: phoneNumbers,
    append: addPhoneNumber,
    remove: removePhoneNumber,
  } = useFieldArray({
    control,
    name: 'receiver_numbers',
  })

  const handleAddPhoneNumber = () => {
    if (phoneNumbers.length < MAX_RECEIVER_NUMBERS_AMOUNT) {
      addPhoneNumber({ id: generateRandomId(), country: defaultCountryCode, phone: '' })
    }
  }

  function handleRemovePhoneNumber(index: number) {
    // Get current form values to avoid stale data
    const currentReceiverNumbers = watch('receiver_numbers')

    if (index === 0 && currentReceiverNumbers.length === 1) {
      // If it's the first and only phone number, clear it instead of removing
      const currentPhone = currentReceiverNumbers[index]
      setValue(
        `receiver_numbers.${index}`,
        {
          ...currentPhone,
          phone: '',
          country: defaultCountryCode,
        },
        {
          shouldDirty: true,
          shouldTouch: true,
        },
      )
      // Force re-render of phone number fields
      setPhoneNumberKey((prev) => prev + 1)
      // Clear validation error for this field
      setPhoneValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[index]
        return newErrors
      })
    } else {
      // Otherwise, remove the phone number normally
      removePhoneNumber(index)
      // Clear validation error for this field
      setPhoneValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[index]
        return newErrors
      })
    }
  }

  const onSubmit = (data: SendTestReminderFormData) => {
    // Validate message length
    if (messageLength > maxMessageLength) {
      setError('reminder_message', {
        type: 'custom',
        message: `Default SMS Reminder must be shorter than ${maxMessageLength} characters`,
      })
      return
    }

    // Check for phone validation errors
    if (Object.keys(phoneValidationErrors).length > 0) {
      setError('receiver_numbers', {
        type: 'custom',
        message: 'Please fix phone number validation errors before sending',
      })
      return
    }

    // Construct payload
    const payload: SendTestReminderPayload = {
      event_id: event.id,
      title: event.title,
      event_date: event.event_date,
      sender_name: profileData.default_message_template.sender_name,
      location: data.location,
      reminder_message: data.reminder_message,
      receiver_numbers: [],
    }

    // Ensure unique phone numbers
    const seenNumbers = new Set()
    for (const receiver of data.receiver_numbers) {
      const phoneNumber = `+${receiver.phone.replace(/\D/g, '')}`
      if (!seenNumbers.has(phoneNumber) && phoneNumber.length >= 10) {
        seenNumbers.add(phoneNumber)
        payload.receiver_numbers.push(phoneNumber)
      }
    }

    onSend(payload)
  }

  function customOnSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    console.log(JSON.stringify(errors))
    handleSubmit(onSubmit)(e)
  }

  return (
    <>
      <Modal
        style={{ maxWidth: '720px' }}
        onClose={onClose}
        title={
          <Typography variant="h3" className={classes.modalTitle}>
            {modalTitle}
          </Typography>
        }
      >
        <form className={classNames(classes.root)} onSubmit={customOnSubmit}>
          <div className={classes.header}>
            <div>
              <Typography variant="h2" className={classes.title}>
                {event.title || <FormattedText id="reminder.noTitle" />}
              </Typography>
              <Typography variant="body" className={classes.regular}>
                {formatEventDate(eventDate, profileData.google_calendar_date_order)},{' '}
                {event.all_day ? <FormattedText id="reminder.allDay" /> : format(eventDate, 'h:mma')}
              </Typography>
            </div>
          </div>
          {error && (
            <Box marginBottom={3}>
              <Typography variant="body" className={classes.error}>
                {error}
              </Typography>
            </Box>
          )}
          {phoneNumbers.map((phone, index) => (
            <FormControl
              label={`${formatMessage({ id: 'onboarding.sendTestReminder.form.phone.label' })} ${index + 1}`}
              error={
                errors.receiver_numbers?.[index]?.phone.message
                  ? errors.receiver_numbers[index]?.phone.message
                  : phoneValidationErrors[index]
              }
              className={classes.textFieldWrapper}
              key={phone.id}
            >
              <Controller
                control={control}
                name={`receiver_numbers.${index}`}
                render={(props) => (
                  <Box display="flex" alignItems="center" gridColumnGap={10}>
                    <PhoneNumberField
                      key={`${phone.id}-${phoneNumberKey}`}
                      value={props.field.value?.phone || ''}
                      defaultCountry={defaultCountryCode}
                      error={
                        errors.receiver_numbers?.[index]?.phone.message
                          ? errors.receiver_numbers[index]?.phone.message
                          : phoneValidationErrors[index]
                      }
                      onChange={(value: string, countryCode: string | null) => {
                        props.field.onChange({
                          country: countryCode,
                          phone: `+${value.replace(/\D/g, '')}`,
                        })

                        // Trigger phone validation after field update
                        setTimeout(() => {
                          const currentReceiverNumbers = watch('receiver_numbers')
                          const phoneNumbers =
                            currentReceiverNumbers
                              ?.map((n) => n.phone)
                              .filter((phone) => phone && phone.trim() !== '' && phone !== '+') || []

                          if (validationTimeoutRef.current) {
                            clearTimeout(validationTimeoutRef.current)
                          }

                          validationTimeoutRef.current = setTimeout(() => {
                            setPhoneNumbersToValidate(phoneNumbers)
                          }, 300)
                        }, 0)
                      }}
                      inputClass={classes.phoneNumberInput}
                    />
                    <span onClick={() => handleRemovePhoneNumber(index)}>
                      <Icon name="close" className={classes.removeButton} />
                    </span>
                  </Box>
                )}
              />
            </FormControl>
          ))}
          {amountOfReceiverNumbers < MAX_RECEIVER_NUMBERS_AMOUNT && (
            <Typography variant="body" className={classes.textButton} onClick={handleAddPhoneNumber}>
              + <FormattedText id="configuration.editReminderModal.textButton.addPhone" />
            </Typography>
          )}
          <TextField
            wrapperClassName={classes.textFieldWrapper}
            label="Meeting Location"
            Element="textarea"
            {...register('location')}
            error={errors.location?.message}
          />
          <MessageInput
            wrapperClassName={classes.textFieldWrapper}
            label={formatMessage({
              id: 'configuration.editReminderModal.label.message',
            })}
            rows={3}
            {...register('reminder_message')}
            error={errors.reminder_message?.message}
            confirmationsEnabled={profileData.appointment_confirmation}
            messageLanguage={messageLanguage}
            messagePreview={messagePreview}
            messageLength={messageLength}
            segmentsCount={segmentsCount}
          />

          <Button type="submit" className={classes.button} widthType="content">
            <FormattedText id="onboarding.sendTestReminder.form.button" />
          </Button>
        </form>
      </Modal>
    </>
  )
}
